import { ValueSkeleton } from '@shared/ui/skeleton';
import { Loader } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { lazy, Suspense, type PropsWithChildren } from 'react';

import { closeSettings, openSettings, SettingsDialog, useSettingsDialog } from '@features/settings';
import { getPartyRoom } from '@entities/party-room';
import { getBattleRoom } from '@entities/battle-room';

import { getSoloProgress } from '@entities/game-session';
import { usePublishedCampaign } from '@entities/campaign';
import { useCollectionName } from '@entities/collection';
import { readCategorySelection } from '@entities/category';
import { categories } from '@shared/config/categories';
import { BackLink, BackButton } from '@shared/ui/page-header';

import { brandTagline } from '@shared/config/brand-tagline';
import { getGameResourceOwner, useGameSession } from '@shared/lib/auth/game-session';
import { getViewerIdentity, hasAccountSession } from '@shared/lib/auth/viewer-identity';
import { developmentMode, formatUznabucks, LifeIcon, useGameResources } from '@entities/game-resource';
import { useShopCatalog } from '@entities/shop-catalog';

import { backNavigation } from '../model/back-navigation';

import coinsIcon from './icons/coins.svg';
import entryIcon from './icons/entry.svg';
import gamepadIcon from './icons/gamepad.svg';
import trophyIcon from './icons/trophy.svg';
import classes from './app-shell.module.css';

const DevelopmentModeToggle =
  import.meta.env.DEV || import.meta.env.MODE === 'development'
    ? lazy(() =>
        import('@features/development-tools').then(({ DevelopmentModeToggle: Toggle }) => ({ default: Toggle })),
      )
    : null;

const primaryNavigation = [
  { to: '/rankings', label: 'Рейтинги', icon: trophyIcon },
  { to: '/shop', label: 'Магазин', icon: coinsIcon },
  { to: '/play', label: 'Играть', icon: gamepadIcon },
] as const;

const ResourceLinks = observer(({ login, serverGame }: { login?: string; serverGame: boolean }) => {
  const resources = useGameResources(login);
  const unlimitedLives = developmentMode.enabled && !serverGame;
  return (
    <div className={classes.resourceLinks}>
      <Link
        to="/shop"
        hash="lives"
        className={classes.livesLink}
        aria-label={
          unlimitedLives
            ? 'Бесконечные жизни. God mode включён'
            : resources.loaded
              ? `Жизни: ${resources.lives} из ${resources.maxLives}. Открыть пополнение в магазине`
              : 'Загружаем жизни. Открыть магазин'
        }
      >
        <LifeIcon size={32} empty={!unlimitedLives && resources.loaded && resources.lives === 0} />
        <span aria-hidden="true">
          {unlimitedLives ? (
            '∞'
          ) : resources.loaded ? (
            `${resources.lives}/${resources.maxLives}`
          ) : resources.error ? (
            '—'
          ) : (
            <ValueSkeleton width={46} label="Загружаем жизни" />
          )}
        </span>
      </Link>
      <Link
        to="/shop"
        className={classes.currencyLink}
        aria-label={
          resources.loaded
            ? `${formatUznabucks(resources.coins)}. Открыть магазин`
            : 'Загружаем баланс. Открыть магазин'
        }
      >
        <img src="/uznabuck.webp" alt="" aria-hidden="true" />
        <span aria-hidden="true">
          {resources.loaded ? (
            resources.coins
          ) : resources.error ? (
            '—'
          ) : (
            <ValueSkeleton width={36} label="Загружаем баланс" />
          )}
        </span>
      </Link>
    </div>
  );
});

const CollectionNavigation = observer(({ packId, onBack }: { packId: string; onBack: () => void }) => {
  const name = useCollectionName(packId);
  return (
    <div className={classes.gameNavigation} data-collection>
      <BackButton onClick={onBack} label="К подборкам" compact />
      <div className={classes.gameHeading}>
        <strong title={name ?? undefined}>{name ?? <ValueSkeleton width={128} label="Загружаем подборку" />}</strong>
        <span>Подборки</span>
      </div>
    </div>
  );
});

const CampaignNavigation = observer(({ categoryId, chapterId }: { categoryId: string; chapterId: string }) => {
  const campaign = usePublishedCampaign();
  const category = categories.find(({ id }) => id === categoryId);
  const chapter = campaign.chapters.find(({ id }) => id === chapterId);
  return (
    <div className={classes.gameNavigation}>
      <BackLink to="/play/campaigns/$categoryId" categoryId={categoryId} label="Вернуться к главам" compact />
      <div className={classes.gameHeading}>
        <strong>{category?.label}</strong>
        <span>
          {chapter?.name ??
            (campaign.error ? 'Глава недоступна' : <ValueSkeleton width={96} label="Загружаем главу" />)}
        </span>
      </div>
    </div>
  );
});

const EndlessNavigation = observer(({ playing, categoryId }: { playing: boolean; categoryId?: string }) => {
  const progress = getSoloProgress({ mode: 'endless' }, getGameResourceOwner() ?? '');
  const selected = readCategorySelection(getViewerIdentity()?.login);
  const category =
    categoryId && categoryId !== 'game'
      ? categories.find((item) => item.id === categoryId)
      : playing && progress.currentQuestionId && !progress.complete
        ? categories.find(
            (item) =>
              ({ movies: 'film', games: 'game', series: 'series', anime: 'anime' })[item.id] === progress.currentKind,
          )
        : undefined;
  return (
    <div className={classes.gameNavigation}>
      <BackLink
        to={playing ? '/play/endless' : '/play'}
        label={playing ? 'К категориям бесконечного режима' : 'Назад к игровым режимам'}
        compact
      />
      <div className={classes.gameHeading}>
        <strong>Бесконечный режим</strong>
        {category && selected.includes(category.id) && <span>{category.label}</span>}
      </div>
    </div>
  );
});

const PartyNavigation = observer(() => {
  const room = getPartyRoom();
  if (room.phase === 'setup') return <BackLink to="/play" label="Назад к игровым режимам" compact />;
  return (
    <div className={classes.gameNavigation}>
      <BackButton label="Назад к настройке Party" onClick={() => room.exit()} compact />
      <div className={classes.gameHeading}>
        <strong>Party</strong>
        <span>Комната {room.code}</span>
      </div>
    </div>
  );
});

const BattleNavigation = observer(() => {
  const room = getBattleRoom();
  if (room.phase === 'setup') return <BackLink to="/play" label="Назад к игровым режимам" compact />;
  return (
    <div className={classes.gameNavigation}>
      <BackButton label="Назад к настройке батла" onClick={() => room.exit()} compact />
      <div className={classes.gameHeading}>
        <strong>Батлы</strong>
        <span>Комната {room.code}</span>
      </div>
    </div>
  );
});

export const AppShell = observer(({ children }: PropsWithChildren) => {
  const session = useGameSession();
  useShopCatalog();
  const { opened: settingsOpened, targetCategoryId } = useSettingsDialog();
  const router = useRouter();
  const handleCloseSettings = () => {
    closeSettings();
    void router.invalidate();
  };
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const collectionPackId = useRouterState({ select: (state) => state.location.search.packId });
  const dailyCategory = pathname.startsWith('/play/daily/')
    ? categories.find((category) => category.id === pathname.split('/')[3])
    : null;
  const chapterRoute = /^\/play\/campaigns\/([^/]+)\/([^/]+)\/?$/.exec(pathname);
  const pendingPath = useRouterState({
    select: (state) => (state.status === 'pending' ? state.location.pathname : null),
  });
  const viewerLogin = getViewerIdentity()?.login;
  const partyRoomActive = pathname === '/play/party' && getPartyRoom().phase !== 'setup';
  const battleRoomActive = pathname === '/play/battles' && getBattleRoom().phase !== 'setup';
  const partyGameActive = partyRoomActive && ['loading', 'question', 'reveal'].includes(getPartyRoom().phase);
  const partyScreenOnly = partyGameActive && getPartyRoom().screenOnly;
  const back = backNavigation(pathname, readCategorySelection(viewerLogin).length);
  const viewerProfilePath = viewerLogin ? `/@${viewerLogin}` : null;
  const authNavigation = hasAccountSession()
    ? ({ to: '/profile', label: 'Профиль', icon: entryIcon } as const)
    : pathname === '/sign-up' || pathname.startsWith('/sign-up/')
      ? ({ to: '/sign-up', label: 'Создать', icon: entryIcon } as const)
      : ({ to: '/sign-in', label: 'Войти', icon: entryIcon } as const);
  const navigation = [...primaryNavigation, authNavigation] as const;
  const activeDockIndex = settingsOpened
    ? 4
    : navigation.findIndex(
        ({ to }) =>
          pathname === to ||
          pathname.startsWith(`${to}/`) ||
          (to === '/profile' &&
            Boolean(
              viewerProfilePath && (pathname === viewerProfilePath || pathname.startsWith(`${viewerProfilePath}/`)),
            )),
      );

  return (
    <div
      className={classes.layout}
      data-home={pathname === '/' || undefined}
      data-campaign={pathname.startsWith('/play/campaigns') || undefined}
      data-rankings={pathname === '/rankings' || undefined}
      data-party-room={partyRoomActive || undefined}
      data-room={partyRoomActive || battleRoomActive || undefined}
      data-party-game={partyGameActive || undefined}
      data-party-screen={partyScreenOnly || undefined}
    >
      <span className={classes.backdropOrbit} aria-hidden="true" />
      <header className={classes.topbar}>
        <div className={classes.topbarInner}>
          {chapterRoute ? (
            <CampaignNavigation categoryId={chapterRoute[1]!} chapterId={chapterRoute[2]!} />
          ) : pathname.startsWith('/play/endless/') ? (
            <EndlessNavigation playing={pathname !== '/play/endless'} categoryId={pathname.split('/')[3]} />
          ) : pathname === '/play/party' ? (
            <PartyNavigation />
          ) : pathname === '/play/battles' ? (
            <BattleNavigation />
          ) : dailyCategory ? (
            <div className={classes.gameNavigation}>
              <BackLink to="/play/daily" label="К категориям Daily" compact />
              <div className={classes.gameHeading}>
                <strong>{dailyCategory.label}</strong>
                <span>Daily</span>
              </div>
            </div>
          ) : pathname === '/play/collections' && collectionPackId ? (
            <CollectionNavigation
              packId={collectionPackId}
              onBack={() => void router.navigate({ to: '/play/collections', search: { packId: undefined } })}
            />
          ) : back ? (
            <BackLink {...back} compact />
          ) : (
            <Link to="/" className={classes.brand} aria-label="Узнали?, главная">
              <span className={classes.brandMark} aria-hidden="true" />
              <span className={classes.brandText}>
                <span className={classes.brandName}>
                  <span className={classes.brandInitial}>У</span>ЗНАЛИ?
                </span>
                <span className={classes.brandTagline}>{brandTagline}</span>
              </span>
            </Link>
          )}

          {(pathname !== '/' || (session && hasAccountSession())) && (
            <ResourceLinks
              login={viewerLogin}
              serverGame={
                pathname.startsWith('/play/campaigns') ||
                pathname.startsWith('/play/endless') ||
                pathname === '/play/collections' ||
                pathname.startsWith('/play/daily')
              }
            />
          )}
        </div>
      </header>

      <SettingsDialog opened={settingsOpened} targetCategoryId={targetCategoryId} onClose={handleCloseSettings} />
      {DevelopmentModeToggle && (
        <Suspense fallback={null}>
          <DevelopmentModeToggle />
        </Suspense>
      )}

      <main className={classes.main} id="main-content">
        <div className={classes.mainContent}>{children}</div>
      </main>

      <nav aria-label="Основная навигация" className={classes.dock} data-active-index={activeDockIndex}>
        <div className={classes.dockInner}>
          <span className={classes.dockIndicator} aria-hidden="true" />
          {navigation.map(({ to, label, icon }, index) => (
            <Link
              key={to}
              to={to}
              className={`${classes.dockLink} ${to === '/play' ? classes.dockPlay : ''}`}
              data-active={activeDockIndex === index ? true : undefined}
              aria-current={activeDockIndex === index ? 'page' : undefined}
            >
              {pendingPath &&
              (pendingPath === to ||
                pendingPath.startsWith(`${to}/`) ||
                (to === '/profile' && pendingPath.startsWith('/@'))) ? (
                <Loader size="sm" aria-label="Загружаем страницу" />
              ) : (
                <span
                  className={classes.dockIcon}
                  style={{ maskImage: `url("${icon}")`, WebkitMaskImage: `url("${icon}")` }}
                  aria-hidden="true"
                />
              )}
              <span>{label}</span>
            </Link>
          ))}
          <button
            type="button"
            className={classes.dockLink}
            data-active={settingsOpened || undefined}
            onClick={openSettings}
            aria-haspopup="dialog"
            aria-expanded={settingsOpened}
          >
            <IconSettings className={classes.dockSettingsIcon} stroke={2} aria-hidden="true" />
            <span>Настройки</span>
          </button>
        </div>
      </nav>
    </div>
  );
});
